import { FC, MouseEvent, useMemo, useState } from "react";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import {
	Box,
	IconButton,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { SATOSLink } from "@/components/Application/SATOSLink";
import { EditFolder } from "@/components/Application/WrapperHeadingLeft";
import { ProcedureListItemDesign } from "@/components/Procedures/ProcedureListItemDesign";

import { CustomTreeItem } from "@/helpers/components/TreeView/CustomTreeItem";

import { Procedure } from "@/model/Procedure/Procedure";
import { ProcedureGroup } from "@/model/Procedure/ProcedureGroup";

import { sortGroupFolders } from "@/service/ProcedureQueries";

interface ProcedureGroupItemProps {
	group: ProcedureGroup;
	root: boolean;
	groupList: ProcedureGroup[];
	allProcedures: Procedure[] | undefined;
	SetProcedureGroupList: (groupList: ProcedureGroup[]) => void;
	i: number;
	expanded: string[];
	// NEW
	searchQuery?: string; // lowercased
	visibleGroupIds?: Set<number>;
	visibleProcedureIds?: Set<number>;
	onlyMatches?: boolean;
	renderProcedureLabel?: (name: string) => React.ReactNode;
}

export const ProcedureGroupItem: FC<ProcedureGroupItemProps> = (props) => {
	const {
		group,
		groupList,
		allProcedures = [],
		expanded,
		searchQuery = "",
		visibleGroupIds,
		visibleProcedureIds,
		onlyMatches = false,
		renderProcedureLabel,
	} = props;

	const theme = useTheme();

	const [openEditFolder, setOpenEditFolder] = useState(false);

	if (!group) return null;

	// children & procedures (pre-filter if requested)
	const subLevelsRaw = useMemo(
		() =>
			groupList
				.filter((g) => g.ParentId === group.Id)
				.sort((a, b) => sortGroupFolders(a, b)),
		[groupList, group.Id],
	);

	const subLevels = useMemo(() => {
		if (!visibleGroupIds) return subLevelsRaw;
		return subLevelsRaw.filter((g) => visibleGroupIds.has(g.Id));
	}, [subLevelsRaw, visibleGroupIds]);

	const proceduresRaw = useMemo(
		() => allProcedures.filter((p) => p.GroupId === group.Id),
		[allProcedures, group.Id],
	);

	const procedures = useMemo(() => {
		if (!visibleProcedureIds || (!searchQuery && !onlyMatches))
			return proceduresRaw;
		return proceduresRaw.filter((p) => visibleProcedureIds.has(p.Id));
	}, [proceduresRaw, visibleProcedureIds, searchQuery, onlyMatches]);

	const hasSub = subLevels.length > 0;
	const hasChildren = procedures.length > 0;

	const isEmpty = !hasSub && !hasChildren;
	const isExpanded = expanded?.includes(String(group.Id));
	const isLinkGroup = Boolean(group.Link);

	const LeadingIcon = isLinkGroup
		? LaunchIcon
		: isEmpty
			? FolderOffOutlinedIcon // empty folder
			: isExpanded
				? FolderOpenOutlinedIcon // open folder (has content)
				: FolderOutlinedIcon; // closed folder (has content)

	const iconColor = isLinkGroup
		? theme.palette.text.secondary
		: isEmpty
			? theme.palette.text.disabled
			: isExpanded
				? theme.palette.primary.main
				: theme.palette.text.secondary;

	const labelRowSx = {
		display: "flex",
		alignItems: "center",
		gap: 1,
		width: "100%",
		px: 1,
		py: 0.55,
		borderRadius: 1.25,
		transition: "background-color .15s ease",
		"&:hover": {
			backgroundColor:
				theme.palette.mode === "light"
					? alpha(theme.palette.text.primary, 0.04)
					: alpha(theme.palette.text.primary, 0.08),
		},
	} as const;

	const nameSx = {
		fontWeight: 800,
		flexGrow: 1,
		whiteSpace: "nowrap" as const,
		overflow: "hidden",
		textOverflow: "ellipsis",
		fontSize: { xs: 12.5, sm: 13.5, md: 14.5 },
		letterSpacing: 0.2,
	};

	const chipSx = {
		height: 22,
		borderRadius: 1.25,
		"& .MuiChip-label": { px: 0.875, fontSize: 12, fontWeight: 700 },
	} as const;

	// simple highlight helper for group name
	const renderHighlighted = (text?: string) => {
		const t = text ?? "";
		if (!searchQuery) return <>{t}</>;
		const idx = t.toLowerCase().indexOf(searchQuery);
		if (idx === -1) return <>{t}</>;
		const before = t.slice(0, idx);
		const match = t.slice(idx, idx + searchQuery.length);
		const after = t.slice(idx + searchQuery.length);
		return (
			<>
				{before}
				<Box
					component="mark"
					sx={{
						px: 0.25,
						borderRadius: 0.5,
						backgroundColor: alpha(theme.palette.warning.main, 0.25),
					}}
				>
					{match}
				</Box>
				{after}
			</>
		);
	};

	// Name element: linked only for link groups, with stopPropagation to not toggle tree
	const NameEl = isLinkGroup ? (
		<SATOSLink
			to={group.Link!}
			target="_blank"
			rel="noopener noreferrer"
			onClick={(e) => e.stopPropagation()}
			style={{ textDecoration: "none", color: "inherit", flexGrow: 1 }}
		>
			<Typography variant="body2" sx={nameSx} title={group.Name}>
				{renderHighlighted(group.Name)}
			</Typography>
		</SATOSLink>
	) : (
		<Typography variant="body2" sx={nameSx} title={group.Name}>
			{renderHighlighted(group.Name)}
		</Typography>
	);

	// Label content used by both types
	const LabelContent = (
		<Box sx={labelRowSx}>
			<LeadingIcon sx={{ mx: 0.75, fontSize: 20, color: iconColor }} />
			{NameEl}
			<AdminProtected>
				<Tooltip title={isLinkGroup ? "Edit link" : "Edit folder"} arrow>
					<IconButton
						size="small"
						sx={{ ml: 0.25, flexShrink: 0 }}
						onClick={(e: MouseEvent<HTMLButtonElement>) => {
							// keep the tree from toggling and the link from navigating
							e.stopPropagation();
							e.preventDefault();
							setOpenEditFolder(true);
						}}
					>
						<EditOutlinedIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</AdminProtected>
		</Box>
	);

	// LINK GROUP (leaf-style; no subtree render)
	if (isLinkGroup) {
		return (
			<>
				<CustomTreeItem itemId={String(group.Id)} label={LabelContent} />
				<EditFolder
					isLink
					open={openEditFolder}
					onClose={() => setOpenEditFolder(false)}
					groupId={group.Id}
				/>
			</>
		);
	}

	// FOLDER GROUP (may have children)
	return (
		<>
			<CustomTreeItem itemId={String(group.Id)} label={LabelContent}>
				{/* procedures in this group */}
				{procedures.map((p) => (
					<ProcedureListItemDesign
						key={p.Id}
						procedure={p}
						renderLabel={renderProcedureLabel}
					/>
				))}

				{/* subfolders */}
				{subLevels.map((sub) => (
					<ProcedureGroupItem
						key={sub.Id}
						group={sub}
						root={false}
						groupList={groupList}
						allProcedures={allProcedures}
						SetProcedureGroupList={props.SetProcedureGroupList}
						i={0}
						expanded={expanded}
						// pass down filters/highlight
						searchQuery={searchQuery}
						visibleGroupIds={visibleGroupIds}
						visibleProcedureIds={visibleProcedureIds}
						onlyMatches={onlyMatches}
					/>
				))}
			</CustomTreeItem>

			<EditFolder
				isLink={false}
				open={openEditFolder}
				onClose={() => setOpenEditFolder(false)}
				groupId={group.Id}
			/>
		</>
	);
};
